import { recordEventWithCheck } from "src/apiService/modules/events";
import { FEATURE_FLAGS } from "src/interfaces/systemState.interface";
import type { AppState } from "src/store";
import {
  getLoadTimingsSnapshot,
  getPageLoadElapsedMs,
} from "src/utils/loadTimings";

let recordedForPageLoad = false;

function currentFeatureFlags(
  user: AppState["system"]["user"]
): Record<string, boolean> {
  const flags: Record<string, boolean> = {};
  for (const flag of FEATURE_FLAGS) {
    flags[flag] = user?.featureFlags?.[flag] === true;
  }
  // Keep flags that the client build does not know yet so the support page can
  // compare them once they are added to FEATURE_FLAGS.
  for (const [flag, enabled] of Object.entries(user?.featureFlags || {})) {
    flags[flag] = enabled === true;
  }
  return flags;
}

/**
 * Writes the first-load timing snapshot to `Events` once per page load.
 *
 * Call only after the shell is usable so the sales/transactions phases have
 * completed. Internal support sessions are skipped by `recordEventWithCheck`.
 */
export function recordInitialLoadTimings(store: {
  getState: () => AppState;
}): void {
  if (recordedForPageLoad) return;

  const user = store.getState().system.user;
  const timings = getLoadTimingsSnapshot();
  if (!user || Object.keys(timings).length === 0) return;

  // Redux can briefly replay a persisted `initialSalesTransactionsSyncComplete`
  // before `DataLoaderManager` resets it. The timers are the source of truth:
  // don't record until both base listeners have delivered their first snapshot.
  if (timings.salesFirstLoad == null || timings.transactionsFirstLoad == null) {
    return;
  }

  recordedForPageLoad = true;
  void recordEventWithCheck({
    type: "load-timing",
    featureFlags: currentFeatureFlags(user),
    timings,
    totalMs: getPageLoadElapsedMs(),
  });
}
